import React, {
  useState, useEffect,
  useCallback, useMemo,
} from 'react';
import * as Styles from './HeaderV2.styles';
import { useResize } from '@/hooks/useResize';

import {
  IconSupport as GatorIconSupport,
  FloatingMenu,
  IconButton,
  Tooltip,
} from 'gatorcomponents';
import { IconButton as IconButtonV2 } from '@/pages/common/v1/IconButton';
import { supportIds } from './HeaderV2.enum';
import MyAlerts from '../MyAlerts/MyAlerts';
import { BackupModal } from './components/BackupModal';
import {
  authActions,
  backupActions, gatorProtectActions, merlinActions, preChatActions, securityActions,
} from '@/redux/modules';
import { useDispatch, useSelector } from 'react-redux';
import { SitelockProvisionedModal } from './components/SitelockProvisionedModal';
import { isBRServer } from '@/utils/Validators/validation';
import { useAiChat } from '@/hooks/AiChat';
import AiHeaderButton from './components/AiHeaderButton/AiHeaderButton';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import {
  ATIVACAO_ENABLE_AI_ALERTS,
  ATIVACAO_N8N_INHOUSE_URLS, ATIVACAO_NEW_HEADER_AI_CTA, ATIVACAO_OPEN_CHAT_ON_LOGIN, ATIVACAO_SUPPORT_HEADER_WITH_AI, FOXTROT_REFER_EARN,
  OPERACOES_ENABLE_CUSTOMER_TIERS,
} from '@/config/GrowthBook/constants';
import { useHistory, useLocation } from 'react-router';
import { localStorageKeys, sessionStorageKeys } from '@/enums/storage/storage.enum';
import useLocale from '@/hooks/useLocale';
import { NewAiHeaderButton } from './components/NewAiHeaderButton';
import { WSS_ROUTES } from './AiChat/AiChat.enum';
import { OPTIONS } from '@/components/Layout/Menu/HeaderV2/components/NewAiHeaderButton/NewAiHeaderButton.types';
import { useChatbot } from '@/contexts/Chatbot';
import { IconSupportHeart } from '@/icons';
import { TierSupportDropdown } from './components/TierSupportDropdown';

const HeaderV2 = ({
  onToggleMenu,
  routeSupport = '/',
  labelSupport = 'sample support',
  handleClick,
  profileItems = [{
    id: 'sample profile id',
    leftIcon: 'IconData',
    leftIconSize: 'md',
    label: 'sample label',
    onClick: () => null,
  }],
  financialItems = [{
    id: 'sample financial id',
    leftIcon: 'IconData',
    leftIconSize: 'md',
    label: 'sample label',
    onClick: () => null,
  }],
  pageTitle = 'sample title',
  financialLabel = 'sample financial',
  profileLabel = 'sample profile',
  myAlertsLabel = 'sample alerts',
  allNotificationsLabel,
  ticketNotificationsLabel,
  seeMoreLabel,
  alertsTooltipLabel,
  noNotificationsLabel,
  referrerLabel,
  routeReferrer,
  headerButtonLabel,
  disableAiHeaderButton = false,
}) => {
  const [alertsLabel, setAlertsLabel] = useState(myAlertsLabel);
  const [profileMenuLabel, setProfileMenuLabel] = useState(profileLabel);

  const view = useResize();
  const location = useLocation();
  const isMobile = view.width < 960;
  const dispatch = useDispatch();
  const { showBackupRedirectModal, productId, redirectFromPreChat } = useSelector(state => state.backup);
  const { hasNewSitelock } = useSelector(state => state.security);

  const [sitelockModalIsClosed, setSiteLockModalIsClosed] = useState(false);

  const history = useHistory();

  const canOpenChatOnLogin = useFeatureIsOn(ATIVACAO_OPEN_CHAT_ON_LOGIN);
  const newChat = useFeatureIsOn(ATIVACAO_NEW_HEADER_AI_CTA);
  const aiSupportBehavior = useFeatureIsOn(ATIVACAO_SUPPORT_HEADER_WITH_AI);
  const enableReferEarn = useFeatureIsOn(FOXTROT_REFER_EARN);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);
  const chatbot = useChatbot();
  const withAiAlerts = useFeatureIsOn(ATIVACAO_ENABLE_AI_ALERTS);
  const enableCustomerTiers = useFeatureIsOn(OPERACOES_ENABLE_CUSTOMER_TIERS);
  const { alertsTypes, alerts } = useSelector(state => state.merlin);
  const { isAOrAATier } = useSelector(state => state.summary);
  const showTierSupportDropdown = enableCustomerTiers && isAOrAATier;

  const { triggerExternalMessage } = useAiChat();
  const { aichat: generalChatLocale } = useLocale();
  const { headerLocale } = generalChatLocale;

  const { handleInitialInteraction } = useAiChat();

  const { visibleAiChat, minimizedChat } = useSelector(state => state.merlin);

  const { routes: routesLocale, template: templateLocale } = useLocale();

  const checkHasNewSitelock = () => dispatch(securityActions.newSiteLock.check.request());

  const getGatorProtectPlans = useCallback(() => {
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  useEffect(() => {
    getGatorProtectPlans();
  }, [getGatorProtectPlans]);

  useEffect(() => {
    if (withAiAlerts && !alertsTypes) {
      dispatch(merlinActions.alerts.getAlertsTypes());
    }
  }, [dispatch, withAiAlerts, alertsTypes]);

  const alertTypesSlugs = useMemo(() => {
    if (!alertsTypes) return { critical: [], warning: [] };
    const allAlerts = Object.values(alertsTypes);
    return {
      critical: allAlerts.filter(alert => alert.severity === 'critical'),
      warning: allAlerts.filter(alert => alert.severity === 'warning'),
    };
  }, [alertsTypes]);

  const checkCriticalAlertsPerSlug = useCallback(() => {
    alertTypesSlugs.critical.forEach((alert) => {
      if (!alert.loaded && !alert.loading && alerts.length === 0) {
        dispatch(merlinActions.alerts.requestAlertPerSlug({ slug: alert.slug }));
      }
    });
  }, [alertTypesSlugs.critical, alerts, dispatch]);

  useEffect(() => {
    if (alertTypesSlugs.critical.length > 0) {
      checkCriticalAlertsPerSlug();
    }
  }, [alertTypesSlugs.critical, checkCriticalAlertsPerSlug]);

  const checkWarningAlertsPerSlug = useCallback(() => {
    const { critical, warning } = alertTypesSlugs;
    const everyCriticalAlertLoaded = critical.every(alert => alert.loaded);
    const hasNoCriticalAlerts = everyCriticalAlertLoaded && alerts.length === 0;

    if (hasNoCriticalAlerts) {
      warning.forEach((alert) => {
        if (!alert.loaded && !alert.loading) {
          dispatch(merlinActions.alerts.requestAlertPerSlug({ slug: alert.slug }));
        }
      });
    }
  }, [alertTypesSlugs, alerts, dispatch]);

  useEffect(() => {
    if (alertTypesSlugs.warning.length > 0) {
      checkWarningAlertsPerSlug();
    }
  }, [alertTypesSlugs.warning, alerts.length, checkWarningAlertsPerSlug]);

  useEffect(() => {
    function setLabelByWidth() {
      if (window.innerWidth < 1280) {
        setAlertsLabel('');
        setProfileMenuLabel('');
      } else {
        setAlertsLabel(myAlertsLabel);
        setProfileMenuLabel(profileLabel);
      }
    }

    setLabelByWidth();

    if (isBRServer) {
      checkHasNewSitelock();
    }

    window.addEventListener('resize', setLabelByWidth);

    return () => {
      window.removeEventListener('resize', setLabelByWidth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myAlertsLabel, profileLabel]);

  const handleToggleMenu = () => {
    onToggleMenu && onToggleMenu();
  };
  const getBackupModalProps = () => ({
    externalId: productId,
    onClose: (willGoToBackup) => {
      dispatch(backupActions.set.showModalBackupRedirect({ showModal: false, productId: null }));

      if (redirectFromPreChat && !willGoToBackup) {
        dispatch(preChatActions.preChat.setScreen('problemSolved'));
        dispatch(backupActions.set.redirectFromPreChat(false));
        dispatch(preChatActions.preChat.setShowPreChat(true));
      }
    },
  });


  const isInitalPage = (location.pathname === routesLocale.home || location.pathname === routesLocale.sitesPage);
  const notShowMaiaOnInit = localStorage.getItem(localStorageKeys.showMaiaOnInit) === 'false' || !isInitalPage;
  const chatOpenedBefore = sessionStorage.getItem(sessionStorageKeys.chatOpenedBefore) === 'true';

  useEffect(() => {
    if (canOpenChatOnLogin && !notShowMaiaOnInit && !chatOpenedBefore) {
      sessionStorage.setItem(sessionStorageKeys.chatOpenedBefore, 'true');
      handleInitialInteraction();
    }
  }, [canOpenChatOnLogin, notShowMaiaOnInit, chatOpenedBefore, handleInitialInteraction]);

  useEffect(() => {
    if (chatbot.isActive) {
      chatbot.updateChatAlerts({ alertsTypes: alertsTypes || {}, alerts: alerts || [] });
    }
  }, [chatbot, alertsTypes, alerts]);

  const createRefreshTokenInterval = useCallback(() => {
    dispatch(authActions.tokens.jwt.update());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReferrerClick = () => {
    history.push(routeReferrer);
  };

  useEffect(() => {
    createRefreshTokenInterval();
  }, [createRefreshTokenInterval]);

  const referrerBlock = isBRServer && enableReferEarn && (
    <Styles.ReferrerWrapper onClick={handleReferrerClick}>
      <IconSupportHeart />
      <Styles.ReferrerText>
        {referrerLabel}
      </Styles.ReferrerText>
    </Styles.ReferrerWrapper>
  );

  return (
    <Styles.Container>
      {/* TODO: Re-enable this modal after reviewing render conditionals */}
      {/* {(showGatorProtectAcknowledgedModal && !loadingGatorProtect) && (
        <GatorProtectAcknowledgedModal {...getGatorProtectAcknowledgedModalProps()} />
      )} */}

      {showBackupRedirectModal && (
        <BackupModal {...getBackupModalProps()} />
      )}

      {isBRServer && hasNewSitelock && !sitelockModalIsClosed && (
        <SitelockProvisionedModal setSiteLockModalIsClosed={setSiteLockModalIsClosed} />
      )}

      <Styles.Row>
        <Styles.TitleWrapper>
          {isMobile && (
            <Styles.IconWrapper>
              <IconButton icon="IconMenu" onClick={handleToggleMenu} />
            </Styles.IconWrapper>
          )}

          <Styles.PageName title={pageTitle()}>
            {pageTitle()}
          </Styles.PageName>
        </Styles.TitleWrapper>

        {isMobile && referrerBlock}

        {!disableAiHeaderButton && (
          (!visibleAiChat && !minimizedChat) && (
            newChat
              ? <NewAiHeaderButton hideOnMobile />
              : (
                <AiHeaderButton
                  onClick={handleInitialInteraction}
                  headerButtonLabel={headerButtonLabel}
                />
              )
          )
        )}

        <Styles.ActionsWrapper>
          {!isMobile && referrerBlock}

          <Tooltip content={templateLocale.headerTooltips.billing} preferredPosition="bottomLeft">
            <Styles.FirstFloatingWrapper>
              <FloatingMenu
                label={financialLabel}
                items={financialItems}
                testId="financial"
                variant="icon"
                buttonIcon="IconCreditCardV2"
                iconColor="lowDark"
                onlyFillSvg={false}
              />
            </Styles.FirstFloatingWrapper>
          </Tooltip>

          {showTierSupportDropdown ? (
            <TierSupportDropdown
              onChatClick={() => dispatch(preChatActions.preChat.setShowPreChat(true))}
            />
          ) : (
            <Tooltip content={templateLocale.headerTooltips.support} preferredPosition="bottomLeft">
              <IconButtonV2
                icon={<GatorIconSupport color="lowDark" />}
                onClick={() => {
                  if (aiSupportBehavior) {
                    if (!chatbot.isActive) {
                      const route = wssToggleRoutes.general || WSS_ROUTES.GENERAL;
                      dispatch(merlinActions.inhouse.setRoute(route));
                    } else {
                      chatbot.setRoute('general');
                    }
                    const supportItem = headerLocale.options.find(item => item.value === OPTIONS.SUPPORT);
                    triggerExternalMessage({ message: supportItem.message, fullScreen: window.Chatbot.isFullscreen });
                  }

                  if (!aiSupportBehavior) {
                    if (chatbot.isActive) {
                      chatbot.close();
                    }
                    handleClick({ id: supportIds.support, route: routeSupport, label: labelSupport });
                  }
                }}
                version="v2"
                testId="header-support-icon"
              />
            </Tooltip>
          )}

          <Tooltip content={templateLocale.headerTooltips.notifications} preferredPosition="bottomLeft">
            <Styles.FirstFloatingWrapper>
              <MyAlerts
                label={alertsLabel}
                titleLabel={myAlertsLabel}
                allNotificationsLabel={allNotificationsLabel}
                ticketNotificationsLabel={ticketNotificationsLabel}
                seeMoreLabel={seeMoreLabel}
                alertsTooltipLabel={alertsTooltipLabel}
                noNotificationsLabel={noNotificationsLabel}
                isMobile={isMobile}
              />
            </Styles.FirstFloatingWrapper>
          </Tooltip>

          <Tooltip content={templateLocale.headerTooltips.profile} preferredPosition="bottomLeft">
            <Styles.LastFloatingWrapper>
              <FloatingMenu
                label={profileMenuLabel}
                items={profileItems}
                testId="profile"
                variant="icon"
                buttonIcon="IconPerson"
                iconColor="lowDark"
                onlyFillSvg={false}
              />
            </Styles.LastFloatingWrapper>
          </Tooltip>
        </Styles.ActionsWrapper>
      </Styles.Row>
    </Styles.Container>
  );
};

export default HeaderV2;
